import { Injectable } from '@angular/core';
import {
	PluginType,
	PredictionTriviaGameData,
	TriviaParagraph,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Prediction trivia specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class PredictionTriviaGenerationService
	extends BaseGenerationService<PredictionTriviaGameData>
{
	readonly pluginType = PluginType.PredictionTrivia;

	/**
	 * Create system and user prompts for prediction trivia generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;

		// Get rewrite level from parameters
		const rewriteLevel = parameters['rewriteLevel'] || 'deny';
		const splitCount = parameters['splitCount'] || 5;
		const amountOfAnswers = parameters['amountOfAnswers'] || 3;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// System prompt for JSON formatting
		const systemPrompt = `Anna vastauksesi JSON-objektina. Käytä rivinvaihtoihin ja kappalemerkintöihin HTML-muotoilua eli <p>- ja <br>-tageja. Käyttäjä kertoo, mitä avaimia JSON-formaatissa tulee olla. Et saa lisätä JSON-avaimen sisälle uusia avaimia. Vastauksen tulee olla validoitu JSON-objekti. Vain pyydettyjä avaimia saa käyttää.

${targetLanguagePrompt}`;

		// Select user prompt based on rewrite level
		let userPromptTemplate: string;

		switch (rewriteLevel) {
			case 'deny':
				userPromptTemplate = `Annan sinulle yhden tai useamman artikkelin. Käytä file_search-työkalua tutkiaksesi sen tekstiä.
      
1) Keksi pelille kuvaava nimi, joka näytetään pelin yläosassa.

2) Jaa artikkeli tasan ${splitCount} kokonaisuuteen. Jokaisen kokonaisuuden pitäisi olla suunnilleen samankokoinen. Jätä kappaleiden mahdolliset alkuperäiset otsikot mukaan kokonaisuuksiin. Et saa muuttaa artikkelin tekstiä ja kirjoitusasua. Varmista, että kokonaisuudet ovat loogisia ja yhtenäisiä, mutta pyri jakamaan ne suunnilleen yhtä suuriin osiin.

3) Luo jokaisesta kokonaisuudesta kysymys. Tämä kysymys kysytään käyttäjältä ennen kuin hän lukee kappaleen. Sen tulee siis olla arvattavissa ennen kappaleen lukemista.

4) Luo jokaiseen kysymykseen ${amountOfAnswers} vastausvaihtoehtoa. Ensimmäisen vastausvaihtoehdon tulee olla oikea vastaus. Muiden vastausten tulee olla selkeästi vääriä. Väärien vastausten pitää kuitenkin liittyä aiheeseen, mutta ne eivät saa olla totta artikkelin kontekstissa. Vastauksen tulee olla kokonainen lause, josta selviää myös kysymys. Muotoile vastaukset kokonaisiksi lauseiksi.

5) Lopuksi luo lyhyt alkuteksti, joka esittelee aiheen yleisesti. Pidä alkuteksti lyhyenä (1-2 kappaletta) ja yleisellä tasolla - älä anna suoria vastauksia kysymyksiin tai paljasta oikeita vastauksia. Alkutekstin tulee olla riittävän yleinen, ettei se pilaa pelin yllätystä. Ota huomioon jo luodut kysymykset ja vastaukset, jotta alkuteksti ei spoilaa niitä.

JSON-avaimet:
"pelinNimi" // string, pelin kuvaava nimi
"alkuteksti" // string, lyhyt johdanto aiheeseen
"kysymykset" // taulukko, jossa ${splitCount} objektia seuraavilla avaimilla:
      - "otsikko" // string, kappaleen otsikko
      - "kappale" // string, alkuperäinen artikkelikappale
      - "kysymys" // string, kappaleeseen liittyvä kysymys
      - "vastaukset" // taulukko string-muotoisia vastausvaihtoehtoja, ensimmäinen on oikea vastaus

${targetLanguagePrompt}`;
				break;

			case 'allow-limited':
				userPromptTemplate = `Annan sinulle yhden tai useamman artikkelin. Käytä file_search-työkalua tutkiaksesi sen tekstiä.

1) Keksi pelille kuvaava nimi, joka näytetään pelin yläosassa.

2) Luo ensin kysymykset ja vastaukset artikkelista. Tämän jälkeen luo lyhyt alkuteksti, joka esittelee aiheen yleisesti. Pidä alkuteksti lyhyenä (1-2 kappaletta) ja yleisellä tasolla - älä anna suoria vastauksia kysymyksiin tai paljasta oikeita vastauksia. Alkutekstin tulee olla riittävän yleinen, ettei se pilaa pelin yllätystä.

3) Jaa artikkeli tasan ${splitCount} asiakokonaisuuteen. Kirjoita nämä asiakokonaisuudet uusiksi selkeällä kielellä. Keskity asioihin ja pyri siihen, että kokonaisuudet ovat suunnilleen samankokoisia. Käytä journalistista kieltä ja kirjoita lyhyesti. Vältä pitkiä lauseita ja monimutkaisia rakenteita. Kirjoita preesens-aikamuodossa (esim. "hän kirjoittaa"). Käytä rivinvaihtoihin ja kappalemerkintöihin HTML-muotoilua eli <p>- ja <br>-tageja.

3) Luo jokaisesta kokonaisuudesta kysymys. Tämä kysymys kysytään käyttäjältä ennen kuin hän lukee kappaleen. Sen tulee siis olla arvattavissa ennen kappaleen lukemista.

4) Luo jokaiseen kysymykseen ${amountOfAnswers} vastausvaihtoehtoa. Ensimmäisen vastausvaihtoehdon tulee olla oikea vastaus. Muiden vastausten tulee olla selkeästi vääriä. Väärien vastausten pitää kuitenkin liittyä aiheeseen, mutta ne eivät saa olla totta artikkelin kontekstissa. Vastauksen tulee olla kokonainen lause, josta selviää myös kysymys. Muotoile vastaukset kokonaisiksi lauseiksi.

5) Lopuksi luo lyhyt alkuteksti, joka esittelee aiheen yleisesti. Pidä alkuteksti lyhyenä (1-2 kappaletta) ja yleisellä tasolla - älä anna suoria vastauksia kysymyksiin tai paljasta oikeita vastauksia. Alkutekstin tulee olla riittävän yleinen, ettei se pilaa pelin yllätystä. Ota huomioon jo luodut kysymykset ja vastaukset, jotta alkuteksti ei spoilaa niitä.

JSON-avaimet:
"pelinNimi" // string, pelin kuvaava nimi
"alkuteksti" // string, lyhyt johdanto aiheeseen
"kysymykset" // taulukko, jossa ${splitCount} objektia seuraavilla avaimilla:
      - "otsikko" // string, kappaleen otsikko
      - "kappale" // string, muokattu asiakokonaisuus
      - "kysymys" // string, kappaleeseen liittyvä kysymys
      - "vastaukset" // taulukko string-muotoisia vastausvaihtoehtoja, ensimmäinen on oikea vastaus

${targetLanguagePrompt}`;
				break;

			case 'allow-full':
				userPromptTemplate = `Annan sinulle yhden tai useamman artikkelin. Käytä file_search-työkalua tutkiaksesi sen tekstiä.

1) Keksi pelille kuvaava nimi, joka näytetään pelin yläosassa.

2) Luo ensin kysymykset ja vastaukset artikkelista. Tämän jälkeen luo lyhyt alkuteksti, joka esittelee aiheen yleisesti. Pidä alkuteksti lyhyenä (1-2 kappaletta) ja yleisellä tasolla - älä anna suoria vastauksia kysymyksiin tai paljasta oikeita vastauksia. Alkutekstin tulee olla riittävän yleinen, ettei se pilaa pelin yllätystä.

3) Jaa artikkeli tasan ${splitCount} asiakokonaisuuteen. Kirjoita nämä asiakokonaisuudet kokonaan uusiksi tiivistetyssä ja selkeässä muodossa. Keskity olennaisiin asioihin ja poista epäolennaiset yksityiskohdat. Jos alkuperäisessä tekstissä on selkeä puhuja, häivytä puhuja ja kirjoita asioista yleisellä tasolla. Jos kyseessä on seminaaritallenne, jossa on eri puhujia, älä viittaa seminaariin vaan kirjoita tällöinkin asioista yleisellä tasolla. Käytä journalistista kieltä ja kirjoita lyhyesti. Vältä pitkiä lauseita ja monimutkaisia rakenteita. Kirjoita preesens-aikamuodossa. Käytä rivinvaihtoihin ja kappalemerkintöihin HTML-muotoilua eli <p>- ja <br>-tageja.

3) Luo jokaisesta kokonaisuudesta kysymys. Tämä kysymys kysytään käyttäjältä ennen kuin hän lukee kappaleen. Sen tulee siis olla arvattavissa ennen kappaleen lukemista.

4) Luo jokaiseen kysymykseen ${amountOfAnswers} vastausvaihtoehtoa. Ensimmäisen vastausvaihtoehdon tulee olla oikea vastaus. Muiden vastausten tulee olla selkeästi vääriä. Väärien vastausten pitää kuitenkin liittyä aiheeseen, mutta ne eivät saa olla totta artikkelin kontekstissa. Vastauksen tulee olla kokonainen lause, josta selviää myös kysymys. Muotoile vastaukset kokonaisiksi lauseiksi. Kirjoita kuitenkin lyhyesti ja ytimekkäästi. Vältä pitkiä lauseita ja monimutkaisia rakenteita. Älä toista kysymyksen sisältöä vastauksessa. Esimerkiksi jos kysymys on "Missä peltotöissä kuokka on kätevä?", älä kirjoita "Kuokka on kätevä erityisesti mullan kuohkeuttamisessa" vaan pelkkä "Mullan kuohkeuttamisessa" riittää. Tämä on tietovisa-tyylinen kysely.

JSON-avaimet:
"pelinNimi" // string, pelin kuvaava nimi
"alkuteksti" // string, lyhyt johdanto aiheeseen
"kysymykset" // taulukko, jossa ${splitCount} objektia seuraavilla avaimilla:
      - "otsikko" // string, asiakokonaisuuden otsikko
      - "kappale" // string, uudelleenkirjoitettu ja tiivistetty asiakokonaisuus
      - "kysymys" // string, kappaleeseen liittyvä kysymys
      - "vastaukset" // taulukko string-muotoisia vastausvaihtoehtoja, ensimmäinen on oikea vastaus

${targetLanguagePrompt}`;
				break;

			default:
				userPromptTemplate = `Luo ${splitCount} arvauspeli-kokonaisuutta lähdemateriaalista.

${targetLanguagePrompt}`;
		}

		return { system: systemPrompt, user: userPromptTemplate };
	}

	/**
	 * Process AI response into PredictionTriviaGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(response: any, parameters: any): GenerationResult {
		try {
			const triviaQuestions = response.json?.kysymykset;
			const introText = response.json?.alkuteksti || '';
			const gameName = response.json?.pelinNimi || '';

			if (
				!triviaQuestions ||
				!Array.isArray(triviaQuestions) ||
				triviaQuestions.length === 0
			) {
				return {
					success: false,
					error: 'Invalid response format: no questions found',
					rawResponse: response.rawResponse,
				};
			}

			// Create new game data
			const gameData = new PredictionTriviaGameData();
			gameData.paragraphs = [];
			gameData.introText = introText;
			gameData.gameName = gameName;

			// Process each question
			triviaQuestions.forEach((q: any) => {
				// Validate question structure
				if (!q.otsikko || !q.kappale || !q.kysymys || !q.vastaukset) {
					throw new Error('Invalid question structure');
				}

				if (!Array.isArray(q.vastaukset) || q.vastaukset.length < 2) {
					throw new Error('Question must have at least 2 answers');
				}

				// Create paragraph with answers in original order (first is correct)
				const paragraph: TriviaParagraph = {
					title: q.otsikko,
					paragraph: q.kappale,
					question: q.kysymys,
					answers: q.vastaukset,
					correctAnswer: -1, // Will be set after shuffling
					userAnswer: -1,
				};

				// Shuffle answers and set correct answer index
				const correctAnswer = paragraph.answers[0];
				paragraph.answers.sort(() => Math.random() - 0.5);
				paragraph.correctAnswer =
					paragraph.answers.indexOf(correctAnswer);

				gameData.paragraphs.push(paragraph);
			});

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate prediction trivia specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		if (!hasSourceMaterial) {
			return {
				valid: false,
				error: 'Valitse vähintään yksi artikkeli lähteeksi',
			};
		}

		const splitCount = request.parameters['splitCount'];
		if (splitCount && (splitCount < 2 || splitCount > 20)) {
			return {
				valid: false,
				error: 'Jakamismäärän tulee olla 2-20 välillä',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for prediction trivia
	 */
	getDefaultParameters(): any {
		return {
			splitCount: 5,
			amountOfAnswers: 3,
			rewriteLevel: 'allow-limited',
			selectedInterfaceId: 0,
		};
	}

	/**
	 * Merge new paragraphs with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing paragraphs
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: PredictionTriviaGameData,
		newGameData: PredictionTriviaGameData,
		clearPrevious: boolean,
	): PredictionTriviaGameData {
		const newParagraphs = newGameData.paragraphs || [];

		// Always update intro text if provided
		if (newGameData.introText) {
			existingGameData.introText = newGameData.introText;
		}

		// Always update game name if provided
		if (newGameData.gameName) {
			existingGameData.gameName = newGameData.gameName;
		}

		if (clearPrevious) {
			existingGameData.paragraphs = newParagraphs;
		} else {
			// If first paragraph is empty placeholder, remove it
			if (
				existingGameData.paragraphs.length === 1 &&
				(!existingGameData.paragraphs[0].paragraph ||
					existingGameData.paragraphs[0].paragraph ===
						'<p>Uusi kappale</p>')
			) {
				existingGameData.paragraphs = [];
			}

			// Add new paragraphs to existing ones
			existingGameData.paragraphs.push(...newParagraphs);
		}

		return existingGameData;
	}
}
